/**
 * SoleadLab 2.0 - Layers Panel (Legend)
 *
 * Floating panel matching the original editor's "Legend" panel.
 * Controls layer visibility, per-overlay opacity, indication type filters,
 * and video opacity. Matches the structure from editor (23).html.
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import { useMapStore, useProjectStore, useUIStore, useMonitoringStore, INDICATION_TYPES } from '../../stores';
import type { IndicationType } from '../../stores/projectStore';
import { useIVStore } from '../../stores/ivStore';
import { useIsReadOnlyViewer, useShareSectionAllowed } from '../../hooks/useShareViewer';
// LayoutLabelLevelVisibility is now Record<string, boolean> (dynamic)
import type { DataPointConfig } from '@/types/monitoring';
import { Panel } from '../common';
import { observedOnVisibleDay } from '../../utils/observations';
import { fitMapToOverlay } from '../../utils/overlayFit';
import './LayersPanel.css';

// Truncate a filename for display (strip path, limit length)
function shortName(raw: string, max = 18): string {
  const base = raw.split(/[\\/]/).pop() ?? raw; // strip directory path
  return base.length > max ? base.slice(0, max) + '…' : base;
}

/** Counted types contribute to the "Total" row */
const COUNTED_TYPES = Object.entries(INDICATION_TYPES)
  .filter(([, cfg]) => cfg.counted)
  .map(([type]) => type as IndicationType);

/** Non-counted types shown in a separate "Media" section */
const NONCOUNTED_TYPES = Object.entries(INDICATION_TYPES)
  .filter(([, cfg]) => !cfg.counted)
  .map(([type]) => type as IndicationType);

// Stable empty array to avoid creating new references in selectors
const EMPTY_DATAPOINTS: DataPointConfig[] = [];

/** Standard level colors (key → { label, color }). Used for built-in levels. */
const STANDARD_LEVEL_COLORS: Record<string, { label: string; color: string }> = {
  station:        { label: 'Station',        color: '#ef4444' },
  inverter:       { label: 'Inverter',       color: '#3b82f6' },
  mppt:           { label: 'MPPT',           color: '#06b6d4' },
  dccb:           { label: 'DCCB',           color: '#f97316' },
  string:         { label: 'String',         color: '#ffffff' },
  module:         { label: 'Module',         color: '#a855f7' },
  row:            { label: 'Row',            color: '#ec4899' },
  zone:           { label: 'Zone',           color: '#14b8a6' },
  infrastructure: { label: 'Infrastructure', color: '#9ca3af' },
  unclassified:   { label: 'Unclassified',   color: '#fbbf24' },
};

const RAIL_W    =  72; // px — nav rail width (left or right)
const TICKET_W  = 280; // px — ticket drawer width
const PANEL_W   = 220; // px — LayersPanel width
const MARGIN    =  12; // px — gap between panel and ticket edge

/** Compute default position: aligned with top of right nav rail, just to its left */
function computeTopRightPos() {
  // Mobile: the date toolbar drops below the header (DateFilterToolbar.css
  // media query), so the panel starts below the toolbar instead of under it.
  const y = window.innerWidth <= 560 ? 118 : 60;
  return { x: Math.max(12, window.innerWidth - RAIL_W - PANEL_W - MARGIN), y };
}

export default function LayersPanel() {
  // F-02E-2B: share viewers see no overlay/orthophoto Remove buttons. The
  // dispatcher rejects DELETE writes for share visitors anyway, but the
  // UI shouldn't even hint that delete is a thing. Visibility toggles
  // and opacity sliders are local UI state and remain available.
  const isReadOnly = useIsReadOnlyViewer();
  // F-02E-2B: Data Points belong to the optional Monitoring section. Show
  // them only when monitoring is allowed for the current session — owners
  // and authenticated users always pass; share visitors only when the link
  // includes 'monitoring' in exposed_sections.
  const monitoringAllowed = useShareSectionAllowed('monitoring');
  // R3-D: a share link may expose the digital layout (module grid, string
  // lines, labels). Editors always pass; viewers only with the section.
  const layoutAllowed = useShareSectionAllowed('layout');
  const showDataPointsSection = !isReadOnly || monitoringAllowed;
  // ── Position tracking + ticket avoidance ──────────────────────────────────
  // Ref tracks the panel's current position (updated via onPositionChange on every drag move)
  const initialPos = computeTopRightPos();
  const panelPosRef        = useRef(initialPos);
  const [syncPosition, setSyncPosition] = useState<{ x: number; y: number } | undefined>(undefined);
  const [layoutLevelsExpanded, setLayoutLevelsExpanded] = useState(false);
  const [dpFiltersExpanded, setDpFiltersExpanded] = useState(false);
  const [ivFiltersExpanded, setIvFiltersExpanded] = useState(false);
  // Collapsible Overlays / Orthophotos sections — default open to preserve prior
  // behavior; let users hide a long list once many layers accumulate.
  const [overlaysExpanded, setOverlaysExpanded] = useState(true);
  const [orthosExpanded, setOrthosExpanded] = useState(true);
  const [vectorsExpanded, setVectorsExpanded] = useState(true);
  const leftActiveTicket = useUIStore((s) => s.leftActiveTicket);
  const rightActiveTicket = useUIStore((s) => s.rightActiveTicket);

  // Saved positions — restored when the ticket closes
  const beforeLeftTicketRef  = useRef<{ x: number; y: number } | null>(null);
  const beforeRightTicketRef = useRef<{ x: number; y: number } | null>(null);

  // Left ticket: nudge RIGHT on open, restore on close
  const prevLeftTicket = useRef<string | null>(null);
  useEffect(() => {
    const wasOpen = !!prevLeftTicket.current;
    const isOpen  = !!leftActiveTicket;
    prevLeftTicket.current = leftActiveTicket;

    if (!wasOpen && isOpen) {
      // Nudge right if the panel overlaps the left rail + ticket area.
      // Left ticket right edge = RAIL_W (72) + TICKET_W (280) = 352px from left.
      const prev           = panelPosRef.current;
      const leftTicketEdge = RAIL_W + TICKET_W; // 352px
      const xOverlaps      = prev.x < leftTicketEdge + MARGIN;
      if (xOverlaps) {
        beforeLeftTicketRef.current = { ...prev };
        const next = { ...prev, x: leftTicketEdge + MARGIN };
        panelPosRef.current = next;
        setSyncPosition({ ...next });
      }
    } else if (wasOpen && !isOpen) {
      // Just closed — read the right ticket state directly from the store
      // to guarantee we have the latest value (avoids stale closure).
      const rightIsOpen = !!useUIStore.getState().rightActiveTicket;
      if (beforeLeftTicketRef.current && !rightIsOpen) {
        const restore = beforeLeftTicketRef.current;
        beforeLeftTicketRef.current = null;
        panelPosRef.current = restore;
        setSyncPosition({ ...restore });
      } else {
        beforeLeftTicketRef.current = null; // clear without restoring
      }
    }
  }, [leftActiveTicket]);

  // Right ticket: nudge LEFT on open, restore on close
  const prevRightTicket = useRef<string | null>(null);
  useEffect(() => {
    const wasOpen = !!prevRightTicket.current;
    const isOpen  = !!rightActiveTicket;
    prevRightTicket.current = rightActiveTicket;

    if (!wasOpen && isOpen) {
      // Nudge left if the panel overlaps the right rail + ticket area.
      // Right ticket left edge = window.innerWidth - RAIL_W - TICKET_W.
      // Panel right edge must be to the left of that.
      const prev            = panelPosRef.current;
      const rightTicketLeft = window.innerWidth - RAIL_W - TICKET_W;
      const panelRightEdge  = prev.x + PANEL_W;
      const xOverlaps       = panelRightEdge > rightTicketLeft - MARGIN;
      if (xOverlaps) {
        beforeRightTicketRef.current = { ...prev };
        const safeX = rightTicketLeft - PANEL_W - MARGIN;
        const next = { ...prev, x: Math.max(0, safeX) };
        panelPosRef.current = next;
        setSyncPosition({ ...next });
      }
    } else if (wasOpen && !isOpen) {
      // Just closed — read the left ticket state directly from the store.
      const leftIsOpen = !!useUIStore.getState().leftActiveTicket;
      if (beforeRightTicketRef.current && !leftIsOpen) {
        const restore = beforeRightTicketRef.current;
        beforeRightTicketRef.current = null;
        panelPosRef.current = restore;
        setSyncPosition({ ...restore });
      } else {
        beforeRightTicketRef.current = null;
      }
    }
  }, [rightActiveTicket]);

  // ── Store subscriptions (fine-grained selectors) ─────────────────────────
  // mapStore: data
  const layerVisibility = useMapStore((s) => s.layerVisibility);
  const indicationFilters = useMapStore((s) => s.indicationFilters);
  const activeOverlayId = useMapStore((s) => s.activeOverlayId);
  const activeOrthophotoId = useMapStore((s) => s.activeOrthophotoId);
  const videoOpacity = useMapStore((s) => s.videoOpacity);
  // mapStore: actions (stable refs)
  const toggleLayerVisibility = useMapStore((s) => s.toggleLayerVisibility);
  const layoutLabelLevels = useMapStore((s) => s.layoutLabelLevels);
  const layoutZoomOverride = useMapStore((s) => s.layoutZoomOverride);
  const toggleLayoutLabelLevel = useMapStore((s) => s.toggleLayoutLabelLevel);
  const layoutLabelAddMode = useUIStore((s) => s.layoutLabelAddMode);
  const setLayoutLabelAddMode = useUIStore((s) => s.setLayoutLabelAddMode);
  const layoutLabelSelectMode = useUIStore((s) => s.layoutLabelSelectMode);
  const setLayoutLabelSelectMode = useUIStore((s) => s.setLayoutLabelSelectMode);
  const layoutGridSelectMode = useUIStore((s) => s.layoutGridSelectMode);
  const setLayoutGridSelectMode = useUIStore((s) => s.setLayoutGridSelectMode);
  const dataPointAlarmFilters = useMapStore((s) => s.dataPointAlarmFilters);
  const dataPointShowLabels = useMapStore((s) => s.dataPointShowLabels);
  const toggleDataPointAlarmFilter = useMapStore((s) => s.toggleDataPointAlarmFilter);
  const toggleDataPointShowLabels = useMapStore((s) => s.toggleDataPointShowLabels);
  const toggleIndicationFilter = useMapStore((s) => s.toggleIndicationFilter);
  const showAllIndications = useMapStore((s) => s.showAllIndications);
  const hideAllIndications = useMapStore((s) => s.hideAllIndications);
  const indicationShowResolved = useMapStore((s) => s.indicationShowResolved);
  const toggleIndicationShowResolved = useMapStore((s) => s.toggleIndicationShowResolved);
  const indicationFieldLens = useMapStore((s) => s.indicationFieldLens);
  const setIndicationFieldLens = useMapStore((s) => s.setIndicationFieldLens);
  const indicationViewMode = useMapStore((s) => s.indicationViewMode);
  const setIndicationViewMode = useMapStore((s) => s.setIndicationViewMode);
  const setActiveOverlay = useMapStore((s) => s.setActiveOverlay);
  const setVideoOpacity = useMapStore((s) => s.setVideoOpacity);

  // projectStore: narrow data selectors
  const indications = useProjectStore((s) => s.data.indications);
  const visibleDates = useProjectStore((s) => s.visibleDates);
  const availableDates = useProjectStore((s) => s.availableDates);
  const overlays = useProjectStore((s) => s.data.overlays);
  const orthophotos = useProjectStore((s) => s.data.orthophotos);
  const flightPaths = useProjectStore((s) => s.data.flightPaths);
  const geoPhotos = useProjectStore((s) => s.data.geoPhotos);
  // F-01C: aggregate across siteLayouts[] for legend gating + level counts.
  // siteLayout (the deprecated active-layout alias) is still read for
  // backward-compat fallback when a project hasn't been normalized yet.
  const siteLayouts = useProjectStore((s) => s.data.siteLayouts);
  const siteLayout = useProjectStore((s) => s.data.siteLayout);
  // Effective list: prefer the array, fall back to a single-element wrap of
  // the legacy alias when no array is set.
  const allLayouts = useMemo(() => {
    if (siteLayouts && siteLayouts.length > 0) return siteLayouts;
    if (siteLayout) return [siteLayout];
    return [];
  }, [siteLayouts, siteLayout]);
  const anyLayoutHasTransform = useMemo(
    () => allLayouts.some((l) => !!l.transform),
    [allLayouts],
  );
  // monitoringStore: data points (stable empty array avoids useSyncExternalStore infinite re-render)
  const monitoringDatapoints = useMonitoringStore((s) => s.projectConfig?.datapoints ?? EMPTY_DATAPOINTS);
  const runtimeStates = useMonitoringStore((s) => s.runtimeStates);
  // projectStore: actions (stable refs)
  const toggleOverlayVisibility = useProjectStore((s) => s.toggleOverlayVisibility);
  const setOverlayOpacity = useProjectStore((s) => s.setOverlayOpacity);
  const removeOverlay = useProjectStore((s) => s.removeOverlay);
  const toggleOrthophotoVisibility = useProjectStore((s) => s.toggleOrthophotoVisibility);
  const setOrthophotoOpacity = useProjectStore((s) => s.setOrthophotoOpacity);
  const removeOrthophoto = useProjectStore((s) => s.removeOrthophoto);
  // Plan vectors (rung 3): per-plan show/hide of the operator's picked CAD
  // layers. Session-only in mapStore — never touches overlay.dxf.picked.
  const hiddenVectorOverlayIds = useMapStore((s) => s.hiddenVectorOverlayIds);
  const toggleVectorOverlayVisibility = useMapStore((s) => s.toggleVectorOverlayVisibility);
  const vectorOverlays = overlays.filter(
    (o) => o.dxf?.status === 'ready' && (o.dxf.picked?.length ?? 0) > 0,
  );

  // ── Observed set: indications shown for the currently-selected dates ─────
  // (mirrors the map's date-visibility predicate). Legend counts reflect what's
  // on the map now; the all-dates total lives in the Indications ticket.
  const observedIndications = useMemo(() => {
    const hasAnyDates = availableDates.length > 0;
    const visibleSet = new Set(visibleDates);
    return indications.filter((i) => {
      // PFC-4B: a reviewed Photo container is hidden (its children carry markers).
      if (i.type === 'Photo' && i.properties.reviewed) return false;
      // Observation-aware: credit a persistent fault on every date it was observed.
      if (hasAnyDates && !observedOnVisibleDay(i, visibleSet)) return false;
      return true;
    });
  }, [indications, availableDates, visibleDates]);

  // ── Memoized indication counts over the observed set (O(n) single pass) ──
  // Counts mirror the map's resolution predicate: Current mode with Show-resolved
  // off hides resolved faults from the map, so they drop out of the counts too
  // (the "· N resolved hidden" note keeps the as-found number recoverable).
  // As-found mode ignores resolution, like the map. Type-checkbox state does NOT
  // affect counts — an unchecked type still shows its count next to its toggle.
  const resolvedHidden = indicationViewMode === 'current' && !indicationShowResolved;
  const countMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const ind of observedIndications) {
      if (resolvedHidden && ind.properties.status === 'resolved') continue;
      // The field-changes lens hides untouched marks on the map, so counting
      // them here made the legend read "9" while the map drew 4 — which looks
      // exactly like a filter that does nothing when the untouched marks are
      // off-screen anyway.
      if (indicationFieldLens === 'field' && !ind.properties.lastFieldChangeAt) continue;
      if (indicationFieldLens === 'operator' && ind.properties.lastFieldChangeAt) continue;
      m.set(ind.type, (m.get(ind.type) || 0) + 1);
    }
    return m;
  }, [observedIndications, resolvedHidden, indicationFieldLens]);

  // Resolved among observed counted-type indications (full observed set, before
  // the declutter exclusion) → "· N resolved [hidden]" note.
  const resolvedObservedCount = useMemo(() => {
    const counted = new Set<string>(COUNTED_TYPES);
    return observedIndications.filter((i) => counted.has(i.type) && i.properties.status === 'resolved').length;
  }, [observedIndications]);

  const countedItems = useMemo(() =>
    COUNTED_TYPES.map((type) => ({
      type,
      label: INDICATION_TYPES[type].label,
      color: INDICATION_TYPES[type].color,
      count: countMap.get(type) || 0,
      visible: indicationFilters[type] ?? true,
    })),
  [countMap, indicationFilters]);

  const noncountedItems = useMemo(() =>
    NONCOUNTED_TYPES.map((type) => ({
      type,
      label: INDICATION_TYPES[type].label,
      color: INDICATION_TYPES[type].color,
      count: countMap.get(type) || 0,
      visible: indicationFilters[type] ?? true,
    })),
  [countMap, indicationFilters]);

  const totalCounted = useMemo(() => countedItems.reduce((s, i) => s + i.count, 0), [countedItems]);

  // ── Dynamic label level entries (only levels with entities) ──────────────
  //
  // F-01C: aggregate entity counts AND custom level colors across every
  // layout in `siteLayouts[]`. The legend sub-toggles control rendering for
  // all layouts simultaneously (they map to `mapStore.layoutLabelLevels`,
  // which `useLayoutOverlay` applies per-layout). Counts shown to the user
  // reflect the union of all visible layouts so the legend doesn't
  // misrepresent total surface area.
  const activeLabelLevels = useMemo(() => {
    if (allLayouts.length === 0) return [];
    const counts = new Map<string, number>();
    const mergedCustomColors: Record<string, string> = {};
    for (const l of allLayouts) {
      if (!l.transform) continue;
      for (const e of l.textEntities) {
        const level = e.classification?.level ?? 'unclassified';
        counts.set(level, (counts.get(level) ?? 0) + 1);
      }
      if (l.customLevelColors) {
        for (const [k, v] of Object.entries(l.customLevelColors)) {
          // Last-write-wins on colour collisions across layouts — matches the
          // `customLevelColors` merge order in useLayoutOverlay.
          mergedCustomColors[k] = v;
        }
      }
    }
    const entries: Array<{ key: string; label: string; color: string; count: number }> = [];
    for (const [level, count] of counts) {
      const std = STANDARD_LEVEL_COLORS[level];
      entries.push({
        key: level,
        label: std ? std.label : level.charAt(0).toUpperCase() + level.slice(1),
        color: std ? std.color : (mergedCustomColors[level] ?? '#fbbf24'),
        count,
      });
    }
    // If ANY layout has a visible module grid, ensure "module" appears in the
    // legend even without module-level text entities.
    const anyModuleGrid = allLayouts.some((l) => l.moduleGridConfig?.gridVisible);
    if (anyModuleGrid && !counts.has('module')) {
      const totalStrings = allLayouts.reduce(
        (s, l) => s + (l.transform ? l.textEntities.filter((e) => e.classification?.level === 'string').length : 0),
        0,
      );
      if (totalStrings > 0) {
        entries.push({
          key: 'module',
          label: 'Module Grid',
          color: STANDARD_LEVEL_COLORS.module.color,
          count: totalStrings,
        });
      }
    }

    // Sort: standard levels first (in standard order), then custom alphabetically
    const stdOrder = Object.keys(STANDARD_LEVEL_COLORS);
    entries.sort((a, b) => {
      const ai = stdOrder.indexOf(a.key);
      const bi = stdOrder.indexOf(b.key);
      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return a.key.localeCompare(b.key);
    });
    return entries;
  }, [allLayouts]);

  // ── Data point alarm state counts ──────────────────────────────────────
  const alarmStateCounts = useMemo(() => {
    const counts: Record<string, number> = { ok: 0, warning: 0, critical: 0, stale: 0, no_data: 0 };
    for (const dp of monitoringDatapoints) {
      if (!dp.enabled || !dp.coordinates) continue;
      const rt = runtimeStates.get(dp.id);
      const state = rt?.state ?? 'no_data';
      counts[state] = (counts[state] ?? 0) + 1;
    }
    return counts;
  }, [monitoringDatapoints, runtimeStates]);

  // IV Results layer — per-session
  const ivSessions = useIVStore((s) => s.sessions);
  const ivSessionCache = useIVStore((s) => s.ivSessionCache);
  const loadSessionMeasurements = useIVStore((s) => s.loadSessionMeasurements);
  const ivSessionVisibility = useMapStore((s) => s.ivSessionVisibility);
  const ivLayerVisible = useMapStore((s) => s.ivLayerVisible);
  const toggleIVSessionVisibility = useMapStore((s) => s.toggleIVSessionVisibility);
  const setAllIVSessionsVisibility = useMapStore((s) => s.setAllIVSessionsVisibility);
  const setIVLayerVisible = useMapStore((s) => s.setIVLayerVisible);
  const ivDisplayMetric = useMapStore((s) => s.ivDisplayMetric);
  const setIVDisplayMetric = useMapStore((s) => s.setIVDisplayMetric);
  const ivResultFilters = useMapStore((s) => s.ivResultFilters);
  const toggleIVResultFilter = useMapStore((s) => s.toggleIVResultFilter);

  const ivTotalAssigned = useMemo(
    () => Object.values(ivSessionCache).flat().filter((m) => !!m.map_label).length,
    [ivSessionCache],
  );
  const ivCategoryCounts = useMemo(() => {
    const counts: Record<string, number> = { pass: 0, warn: 0, fail: 0, unknown: 0 };
    for (const measurements of Object.values(ivSessionCache)) {
      for (const m of measurements) {
        if (!m.map_label) continue;
        const key = m.result_pct === null ? 'unknown'
          : m.result_pct >= 0  ? 'pass'
          : m.result_pct >= -5 ? 'warn'
          : 'fail';
        counts[key]++;
      }
    }
    return counts;
  }, [ivSessionCache]);

  const flightPathCount = flightPaths.length;
  const hasVideo = useMemo(() => noncountedItems.some((i) => i.type === 'Video' && i.count > 0), [noncountedItems]);
  const countedWithData = useMemo(() => countedItems.filter((i) => i.count > 0), [countedItems]);
  const noncountedWithData = useMemo(() => noncountedItems.filter((i) => i.count > 0), [noncountedItems]);

  return (
    <Panel
      title="Layers"
      collapsible={true}
      defaultCollapsed={false}
      draggable={true}
      initialPosition={initialPos}
      syncPosition={syncPosition}
      onPositionChange={(pos) => { panelPosRef.current = pos; }}
      width={220}
      className="layers-panel"
    >
      <div className="layers-panel__content">

        {/* ═══════════════════════════════════════════════════════════════
            LAYER TOGGLES
            ═══════════════════════════════════════════════════════════════ */}
        <div className="layers-section">
          {/* Flight Routes */}
          {flightPathCount > 0 && (
            <div className="legend-item">
              <input
                type="checkbox"
                checked={layerVisibility.flightPaths}
                onChange={() => toggleLayerVisibility('flightPaths')}
              />
              <span className="legend-swatch" style={{ background: '#f97316' }} />
              <span className="legend-label">Flight Routes</span>
              <span className="legend-count">{flightPathCount}</span>
            </div>
          )}

          {/* Geo Photos */}
          {geoPhotos.length > 0 && (
            <div className="legend-item">
              <input
                type="checkbox"
                checked={layerVisibility.geoPhotos}
                onChange={() => toggleLayerVisibility('geoPhotos')}
              />
              <span className="legend-swatch" style={{ background: '#3b82f6' }} />
              <span className="legend-label">Geo Photos</span>
              <span className="legend-count">{geoPhotos.length}</span>
            </div>
          )}


          {/* Data Points — master toggle + collapsible alarm state sub-filters.
              F-02E-2B: hidden in share viewer when monitoring is not exposed. */}
          {monitoringDatapoints.length > 0 && showDataPointsSection && (
            <>
              <div className="legend-item">
                <input
                  type="checkbox"
                  checked={layerVisibility.dataPoints}
                  onChange={() => toggleLayerVisibility('dataPoints')}
                />
                <span className="legend-swatch" style={{ background: '#10b981' }} />
                <span
                  className="legend-label legend-label--expandable"
                  onClick={() => setDpFiltersExpanded((v) => !v)}
                >
                  Data Points
                </span>
                <span className="legend-count">{monitoringDatapoints.filter(dp => dp.enabled && dp.coordinates).length}</span>
                <span
                  className={`legend-expand-btn${dpFiltersExpanded ? ' legend-expand-btn--open' : ''}`}
                  onClick={() => setDpFiltersExpanded((v) => !v)}
                  title={dpFiltersExpanded ? 'Collapse' : 'Expand'}
                >
                  &#x25B8;
                </span>
              </div>
              {dpFiltersExpanded && layerVisibility.dataPoints && (
                <div className="legend-sublevel">
                  {([
                    { key: 'critical', label: 'Critical', color: '#EB5757' },
                    { key: 'warning', label: 'Warning', color: '#F2994A' },
                    { key: 'ok', label: 'OK', color: '#27AE60' },
                    { key: 'stale', label: 'Stale', color: '#9CA3AF' },
                    { key: 'no_data', label: 'No Data', color: '#6B7280' },
                  ] as const).map(({ key, label, color }) => (
                    <div className="legend-item legend-item--sub" key={key}>
                      <input
                        type="checkbox"
                        checked={dataPointAlarmFilters[key] ?? true}
                        onChange={() => toggleDataPointAlarmFilter(key)}
                      />
                      <span className="legend-swatch" style={{ background: color }} />
                      <span className="legend-label">{label}</span>
                      <span className="legend-count--sub" style={{ color }}>({alarmStateCounts[key] ?? 0})</span>
                    </div>
                  ))}
                  <div className="legend-item legend-item--sub">
                    <input
                      type="checkbox"
                      checked={dataPointShowLabels}
                      onChange={toggleDataPointShowLabels}
                    />
                    <span className="legend-swatch" style={{ background: 'transparent', border: '1px dashed #999' }} />
                    <span className="legend-label">Show Labels</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Layout Labels — master toggle + collapsible per-level sub-toggles.
              F-01C: gated on ANY layout having a transform, controls all layouts.
              Share viewers see it only when the link exposes the 'layout'
              section (R3-D, owner 2026-09-27) — MapCanvas gates
              useLayoutOverlay on the same section, so the toggles are live
              exactly when the surface is drawn. The edit actions below stay
              editor-only. */}
          {(!isReadOnly || layoutAllowed) && anyLayoutHasTransform && (
            <>
              <div className="legend-item">
                <input
                  type="checkbox"
                  checked={layerVisibility.layoutLabels}
                  onChange={() => toggleLayerVisibility('layoutLabels')}
                />
                <span className="legend-swatch" style={{ background: '#64748b' }} />
                <span
                  className="legend-label legend-label--expandable"
                  onClick={() => setLayoutLevelsExpanded((v) => !v)}
                >
                  Layout Labels
                </span>
                <span
                  className={`legend-expand-btn${layoutLevelsExpanded ? ' legend-expand-btn--open' : ''}`}
                  onClick={() => setLayoutLevelsExpanded((v) => !v)}
                  title={layoutLevelsExpanded ? 'Collapse' : 'Expand'}
                >
                  &#x25B8;
                </span>
              </div>
              {layoutLevelsExpanded && layerVisibility.layoutLabels && (
                <div className="legend-sublevel">
                  {activeLabelLevels.map(({ key, label, color, count }) => (
                    <div className="legend-item legend-item--sub" key={key}>
                      <input
                        type="checkbox"
                        checked={layoutLabelLevels[key] ?? true}
                        onChange={() => toggleLayoutLabelLevel(key)}
                      />
                      <span className="legend-swatch" style={{ background: color }} />
                      <span className="legend-label">{label}</span>
                      <span className="legend-count--sub" style={{ color }}>({count})</span>
                    </div>
                  ))}
                  {/* String Lines — derived polarity-order lines; independent
                      of the Module Grid toggle (grid only / lines only / both). */}
                  <div className="legend-item legend-item--sub">
                    <input
                      type="checkbox"
                      checked={layerVisibility.stringLines !== false}
                      onChange={() => toggleLayerVisibility('stringLines')}
                    />
                    <span className="legend-swatch" style={{ background: 'linear-gradient(90deg,#f43f5e,#f59e0b,#22d3ee)' }} />
                    <span className="legend-label">String Lines</span>
                  </div>
                  {/* All-zooms override — whole-site assessment while
                      positioning: grid + lines ignore the zoom ladder. */}
                  <div className="legend-item legend-item--sub">
                    <input
                      type="checkbox"
                      checked={layoutZoomOverride}
                      onChange={() => useMapStore.getState().toggleLayoutZoomOverride()}
                    />
                    <span className="legend-swatch" style={{ background: '#475569' }} />
                    <span className="legend-label" title="Show the module grid and string lines at every zoom level (labels keep their zoom behavior)">Show at all zooms</span>
                  </div>
                  {!isReadOnly && (
                  <div className="legend-label-actions">
                    <button
                      className={`legend-add-label-btn${layoutLabelAddMode ? ' legend-add-label-btn--active' : ''}`}
                      onClick={() => setLayoutLabelAddMode(!layoutLabelAddMode)}
                      type="button"
                      title={layoutLabelAddMode ? 'Cancel adding label' : 'Click on map to add a new label'}
                    >
                      {layoutLabelAddMode ? '+ Adding...' : '+ Add Label'}
                    </button>
                    <button
                      className={`legend-add-label-btn${layoutLabelSelectMode ? ' legend-add-label-btn--active' : ''}`}
                      onClick={() => setLayoutLabelSelectMode(!layoutLabelSelectMode)}
                      type="button"
                      title={layoutLabelSelectMode ? 'Exit area selection mode' : 'Drag area on map to select labels for batch move'}
                    >
                      {layoutLabelSelectMode ? 'Selecting...' : 'Select Labels'}
                    </button>
                    <button
                      className={`legend-add-label-btn${layoutGridSelectMode ? ' legend-add-label-btn--active' : ''}`}
                      onClick={() => setLayoutGridSelectMode(!layoutGridSelectMode)}
                      type="button"
                      title={layoutGridSelectMode ? 'Exit grid selection mode' : 'Drag area on map to select module grids, then drag any selected cell to move them together'}
                    >
                      {layoutGridSelectMode ? 'Selecting...' : 'Select Grid'}
                    </button>
                  </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Layout Image: no legend control any more (owner, 2026-09-27 —
              the layout's own raster is not in use; plan overlays are the
              raster). layerVisibility.layoutImage defaults to off. */}

          {/* IV Results — per-session toggles + display metric selector */}
          {ivSessions.length > 0 && ivTotalAssigned > 0 && (
            <>
              <div className="legend-item">
                {/* Master switch = layer on/off. It never rewrites the
                    per-session selection, so a viewer's untick of one
                    session survives toggling the layer off and on. The
                    selection itself starts from the owner's saved state.
                    Only when NOTHING is selected does switching on select
                    every session (an on-but-empty layer would look broken). */}
                <input
                  type="checkbox"
                  checked={ivLayerVisible}
                  data-testid="layers-iv-master"
                  onChange={(e) => {
                    const next = e.target.checked;
                    if (next) {
                      const sessionIds = ivSessions.map((s) => s.id);
                      const anySelected = sessionIds.some((id) => ivSessionVisibility[id]);
                      if (!anySelected) setAllIVSessionsVisibility(sessionIds, true);
                      sessionIds.forEach((id) => {
                        if ((anySelected ? ivSessionVisibility[id] : true) && !ivSessionCache[id]) {
                          loadSessionMeasurements(id);
                        }
                      });
                    }
                    setIVLayerVisible(next);
                  }}
                />
                <span className="legend-swatch legend-swatch--iv-results" />
                <span
                  className="legend-label legend-label--expandable"
                  title="IV Results"
                  onClick={() => setIvFiltersExpanded((v) => !v)}
                >
                  IV Results
                </span>
                <span className="legend-count">{ivTotalAssigned}</span>
                <span
                  className={`legend-expand-btn${ivFiltersExpanded ? ' legend-expand-btn--open' : ''}`}
                  onClick={() => setIvFiltersExpanded((v) => !v)}
                  title={ivFiltersExpanded ? 'Collapse' : 'Expand'}
                >
                  &#x25B8;
                </span>
              </div>
              {ivFiltersExpanded && (
                <div className="legend-sublevel">
                  {/* Display metric selector */}
                  <div className="iv-display-metric-row">
                    <label className="iv-display-metric-label">Label value</label>
                    <select
                      className="iv-display-metric-select"
                      value={ivDisplayMetric}
                      onChange={(e) => setIVDisplayMetric(e.target.value)}
                    >
                      <option value="result">Result (%)</option>
                      <option value="voltage_oc">Voc (V)</option>
                      <option value="current_sc">Isc (A)</option>
                      <option value="voltage_mpp">Vmpp (V)</option>
                      <option value="current_mpp">Impp (A)</option>
                      <option value="power_mpp">Pmpp (W)</option>
                      <option value="fill_factor">FF (%)</option>
                      <option value="irradiance">Irradiance (W/m²)</option>
                      <option value="module_temp">Temp (°C)</option>
                      <option value="pmax_opc">Pmax OPC (W)</option>
                    </select>
                  </div>

                  {/* Per-session rows */}
                  {ivSessions.map((session) => {
                    const sessionAssigned = (ivSessionCache[session.id] ?? []).filter((m) => !!m.map_label).length;
                    const isVisible = ivSessionVisibility[session.id] ?? false;
                    return (
                      <div className="legend-item legend-item--sub" key={session.id}>
                        <input
                          type="checkbox"
                          checked={isVisible}
                          data-testid={`layers-iv-session-${session.id}`}
                          onChange={() => {
                            if (!isVisible && !ivSessionCache[session.id]) {
                              loadSessionMeasurements(session.id);
                            }
                            toggleIVSessionVisibility(session.id);
                          }}
                        />
                        <span />
                        <span className="legend-label" title={session.name}>{session.name}</span>
                        <span className="legend-count--sub">
                          {ivSessionCache[session.id] ? sessionAssigned : '…'}
                        </span>
                      </div>
                    );
                  })}

                  {/* Category filters — shown when any session is visible */}
                  {Object.values(ivSessionVisibility).some(Boolean) && (
                    <>
                      <div className="iv-display-metric-divider" />
                      {([
                        { key: 'pass',    label: 'Pass',    color: '#22c55e' },
                        { key: 'warn',    label: 'Warning', color: '#f97316' },
                        { key: 'fail',    label: 'Fail',    color: '#ef4444' },
                        { key: 'unknown', label: 'Unknown', color: '#9ca3af' },
                      ] as const).map(({ key, label, color }) => (
                        <div className="legend-item legend-item--sub" key={key}>
                          <input
                            type="checkbox"
                            checked={ivResultFilters[key] ?? true}
                            onChange={() => toggleIVResultFilter(key)}
                          />
                          <span className="legend-swatch" style={{ background: color }} />
                          <span className="legend-label">{label}</span>
                          <span className="legend-count--sub" style={{ color }}>({ivCategoryCounts[key] ?? 0})</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            PNG OVERLAYS (per-overlay controls)
            ═══════════════════════════════════════════════════════════════ */}
        {overlays.length > 0 && (
          <div className="layers-section">
            <div
              className="layers-section__header layers-section__header--clickable"
              onClick={() => setOverlaysExpanded((v) => !v)}
              title={overlaysExpanded ? 'Collapse' : 'Expand'}
            >
              <span className="layers-section__title">Overlays</span>
              <div className="layers-section__header-right">
                <span className="legend-count">{overlays.length}</span>
                <span
                  className={`legend-expand-btn${overlaysExpanded ? ' legend-expand-btn--open' : ''}`}
                >
                  &#x25B8;
                </span>
              </div>
            </div>
            {overlaysExpanded && (
            <div className="legend-list">
              {overlays.map((overlay) => {
                const isActive = activeOverlayId === overlay.id;
                return (
                  <div key={overlay.id}>
                    <div
                      className={`legend-item legend-item--overlay${isActive ? ' legend-item--active' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={overlay.visible}
                        onChange={() => toggleOverlayVisibility(overlay.id)}
                      />
                      <span className="legend-swatch" style={{ background: '#ff00ff' }} />
                      <span
                        className="legend-label legend-label--clickable"
                        title={overlay.name}
                        onClick={() => {
                          const next = isActive ? null : overlay.id;
                          setActiveOverlay(next);
                          // Selecting a plan brings the map to it (owner, 2026-09-27).
                          if (next) fitMapToOverlay(useMapStore.getState().map, overlay);
                        }}
                      >
                        {overlay.name.length > 18
                          ? overlay.name.slice(0, 18) + '...'
                          : overlay.name}
                      </span>
                      {!isReadOnly && (
                        <button
                          className="legend-remove-btn"
                          title="Remove overlay"
                          onClick={() => removeOverlay(overlay.id)}
                          data-testid={`layers-overlay-remove-${overlay.id}`}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                    {/* Per-overlay opacity slider */}
                    <div
                      className="legend-opacity"
                      title="Opacity when zoomed in. Overlays fade automatically as you zoom out so they don't hide the orthophoto."
                    >
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={Math.round(overlay.opacity * 100)}
                        onChange={(e) =>
                          setOverlayOpacity(overlay.id, Number(e.target.value) / 100)
                        }
                        className="legend-opacity__slider"
                      />
                      <span className="legend-opacity__val">
                        {Math.round(overlay.opacity * 100)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            PLAN VECTORS (rung 3 / R3-D) — one row per plan whose operator
            picked CAD layers; the checkbox is a session-only show/hide that
            never touches the picks. Shown to share viewers as well.
            ═══════════════════════════════════════════════════════════════ */}
        {vectorOverlays.length > 0 && (
          <div className="layers-section" data-testid="layers-vectors-section">
            <div
              className="layers-section__header layers-section__header--clickable"
              onClick={() => setVectorsExpanded((v) => !v)}
              title={vectorsExpanded ? 'Collapse' : 'Expand'}
            >
              <span className="layers-section__title">Vectors</span>
              <div className="layers-section__header-right">
                <span className="legend-count">{vectorOverlays.length}</span>
                <span
                  className={`legend-expand-btn${vectorsExpanded ? ' legend-expand-btn--open' : ''}`}
                >
                  &#x25B8;
                </span>
              </div>
            </div>
            {vectorsExpanded && (
            <div className="legend-list">
              {vectorOverlays.map((overlay) => {
                const shown = !hiddenVectorOverlayIds.includes(overlay.id);
                const layerCount = overlay.dxf?.picked?.length ?? 0;
                return (
                  <div key={overlay.id} className="legend-item legend-item--overlay">
                    <input
                      type="checkbox"
                      checked={shown}
                      onChange={() => toggleVectorOverlayVisibility(overlay.id)}
                      data-testid={`layers-vectors-toggle-${overlay.id}`}
                      aria-label={`Show vectors of ${overlay.name}`}
                    />
                    <span className="legend-swatch legend-swatch--vectors" />
                    <span
                      className="legend-label"
                      title={`${overlay.name} - ${layerCount} picked layer${layerCount === 1 ? '' : 's'}`}
                    >
                      {shortName(overlay.name)}
                    </span>
                    <span className="legend-count--sub">({layerCount})</span>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            ORTHOPHOTOS (per-orthophoto controls)
            ═══════════════════════════════════════════════════════════════ */}
        {orthophotos.length > 0 && (
          <div className="layers-section">
            <div
              className="layers-section__header layers-section__header--clickable"
              onClick={() => setOrthosExpanded((v) => !v)}
              title={orthosExpanded ? 'Collapse' : 'Expand'}
            >
              <span className="layers-section__title">Orthophotos</span>
              <div className="layers-section__header-right">
                <span className="legend-count">{orthophotos.length}</span>
                <span
                  className={`legend-expand-btn${orthosExpanded ? ' legend-expand-btn--open' : ''}`}
                >
                  &#x25B8;
                </span>
              </div>
            </div>
            {orthosExpanded && (
            <div className="legend-list">
              {orthophotos.map((ortho) => {
                const displayName = shortName(ortho.fileName || ortho.name);
                const fullName   = ortho.fileName || ortho.name;
                const isActive   = activeOrthophotoId === ortho.id;
                return (
                  <div key={ortho.id}>
                    <div
                      className={`legend-item legend-item--overlay${isActive ? ' legend-item--active' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={ortho.visible}
                        onChange={() => toggleOrthophotoVisibility(ortho.id)}
                      />
                      <span className="legend-swatch" style={{ background: '#a855f7' }} />
                      <span
                        className="legend-label"
                        title={fullName}
                      >
                        {displayName}
                      </span>
                      {!isReadOnly && (
                        <button
                          className="legend-remove-btn"
                          title="Remove orthophoto"
                          onClick={() => removeOrthophoto(ortho.id)}
                          data-testid={`layers-orthophoto-remove-${ortho.id}`}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                    {/* Per-orthophoto opacity slider — ortho.opacity is 0-100 */}
                    <div className="legend-opacity">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={Math.round(ortho.opacity)}
                        onChange={(e) =>
                          setOrthophotoOpacity(ortho.id, Number(e.target.value))
                        }
                        className="legend-opacity__slider"
                      />
                      <span className="legend-opacity__val">
                        {Math.round(ortho.opacity)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            MEDIA LAYERS (non-counted indication types)
            ═══════════════════════════════════════════════════════════════ */}
        {noncountedWithData.length > 0 && (
          <div className="layers-section">
            <div className="legend-list">
              {noncountedWithData.map((item) => (
                <div key={item.type}>
                  <div className="legend-item">
                    <input
                      type="checkbox"
                      checked={item.visible}
                      onChange={() => toggleIndicationFilter(item.type)}
                    />
                    <span className="legend-swatch" style={{ background: item.color }} />
                    <span className="legend-label">{item.label}</span>
                    <span className="legend-count">{item.count}</span>
                  </div>
                  {/* Video opacity slider */}
                  {item.type === 'Video' && hasVideo && (
                    <div className="legend-opacity">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={Math.round(videoOpacity * 100)}
                        onChange={(e) => setVideoOpacity(Number(e.target.value) / 100)}
                        className="legend-opacity__slider"
                      />
                      <span className="legend-opacity__val">
                        {Math.round(videoOpacity * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            INDICATIONS (counted types)
            ═══════════════════════════════════════════════════════════════ */}
        <div className="layers-section layers-section--indications">
          <div className="layers-section__header">
            <span className="layers-section__title">Indications</span>
            <div className="legend-actions">
              <button
                type="button"
                className="legend-actions__btn"
                onClick={showAllIndications}
              >
                All
              </button>
              <button
                type="button"
                className="legend-actions__btn"
                onClick={hideAllIndications}
              >
                None
              </button>
            </div>
          </div>

          {/* Build B: As-found (inspection as first found — resolution ignored) vs
              Current (resolution overlay: ✓ badge + "· N resolved" + declutter). */}
          <div className="legend-viewmode" role="group" aria-label="Indication view mode">
            <button
              type="button"
              className={`legend-viewmode__btn${indicationViewMode === 'current' ? ' legend-viewmode__btn--active' : ''}`}
              onClick={() => setIndicationViewMode('current')}
              title="Current state — resolved faults keep their type color with a ✓; can be decluttered"
            >
              Current
            </button>
            <button
              type="button"
              className={`legend-viewmode__btn${indicationViewMode === 'asFound' ? ' legend-viewmode__btn--active' : ''}`}
              onClick={() => setIndicationViewMode('asFound')}
              title="As found — every fault as the inspection first recorded it (resolution ignored)"
            >
              As found
            </button>
          </div>

          {/* Total row — observed (date-filtered) count + resolved note */}
          {totalCounted > 0 && (
            <div className="legend-item legend-item--total">
              <span />
              <span className="legend-swatch legend-swatch--total" />
              <span className="legend-label legend-label--total">
                Total
                {indicationViewMode === 'current' && resolvedObservedCount > 0 && (
                  <span style={{ fontWeight: 400, color: 'var(--sl-text-muted)', fontSize: '0.85em' }}>
                    {' · '}{resolvedObservedCount} resolved{resolvedHidden ? ' hidden' : ''}
                  </span>
                )}
              </span>
              <span className="legend-count legend-count--total">{totalCounted}</span>
            </div>
          )}

          {/* Counted indication types */}
          <div className="legend-list legend-list--indications">
            {countedWithData.map((item) => (
              <div className="legend-item" key={item.type}>
                <input
                  type="checkbox"
                  checked={item.visible}
                  onChange={() => toggleIndicationFilter(item.type)}
                />
                <span className="legend-swatch" style={{ background: item.color }} />
                <span className="legend-label">{item.label}</span>
                <span className="legend-count">{item.count}</span>
              </div>
            ))}
            {countedWithData.length === 0 && totalCounted === 0 && (
              <p className="legend-empty">
                {resolvedHidden && resolvedObservedCount > 0
                  ? `${resolvedObservedCount} resolved hidden`
                  : 'No indications'}
              </p>
            )}
          </div>

          {/* Show resolved toggle — Current mode only (declutter the ✓ pins). In
              As-found mode resolution is ignored, so every fault is always shown. */}
          {indicationViewMode === 'current' && (
            <div className="legend-item legend-item--resolved">
              <input
                type="checkbox"
                checked={indicationShowResolved}
                onChange={toggleIndicationShowResolved}
              />
              <span className="legend-swatch legend-swatch--resolved" aria-hidden>✓</span>
              <span className="legend-label">Show resolved</span>
              <span className="legend-count" />
            </div>
          )}

          {/* FL-1 review lens: only indications a field link touched. Session-
              local (not persisted) — it's a review filter, not a preference. */}
          {/* FL review lens — who last touched a mark. Three states, because
              "only the office's marks" is as real a question as "only what the
              field did": a reviewer checking a technician's work wants one,
              and someone auditing the original inspection wants the other. */}
          <div className="legend-viewmode" role="group" aria-label="Field changes lens" data-testid="legend-field-changes">
            <button
              type="button"
              className={`legend-viewmode__btn${indicationFieldLens === 'all' ? ' legend-viewmode__btn--active' : ''}`}
              onClick={() => setIndicationFieldLens('all')}
              title="All marks, office and field"
            >
              All
            </button>
            <button
              type="button"
              className={`legend-viewmode__btn${indicationFieldLens === 'field' ? ' legend-viewmode__btn--active' : ''}`}
              onClick={() => setIndicationFieldLens('field')}
              title="Only marks a field technician created or edited"
            >
              🛠 Field
            </button>
            <button
              type="button"
              className={`legend-viewmode__btn${indicationFieldLens === 'operator' ? ' legend-viewmode__btn--active' : ''}`}
              onClick={() => setIndicationFieldLens('operator')}
              title="Only office marks — hides everything the field touched"
            >
              Office
            </button>
          </div>
        </div>
      </div>
    </Panel>
  );
}
